/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type Contact,
  type Message,
  type ThreadDetail,
  type ThreadSummary,
  messagingApi,
} from '../api/messaging.js';

const THREADS_KEY = ['messages', 'threads'] as const;
const threadKey = (id: string): readonly unknown[] => ['messages', 'threads', id];
const CONTACTS_KEY = ['messages', 'contacts'] as const;

export function useThreads(): UseQueryResult<readonly ThreadSummary[]> {
  return useQuery({ queryKey: THREADS_KEY, queryFn: () => messagingApi.listThreads() });
}

export function useContacts(): UseQueryResult<readonly Contact[]> {
  return useQuery({ queryKey: CONTACTS_KEY, queryFn: () => messagingApi.contacts() });
}

export function useThread(id: string | undefined): UseQueryResult<ThreadDetail> {
  return useQuery({
    queryKey: threadKey(id ?? ''),
    queryFn: () => messagingApi.getThread(id ?? ''),
    enabled: id !== undefined && id !== '',
  });
}

export function useStartThread(): UseMutationResult<{ readonly threadId: string }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recipientUserId: string) => messagingApi.startThread(recipientUserId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: THREADS_KEY });
    },
  });
}

interface SendVars {
  readonly threadId: string;
  readonly body: string;
}

export function useSendMessage(): UseMutationResult<Message, Error, SendVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: SendVars) => messagingApi.send(vars.threadId, vars.body),
    onSuccess: (_msg, vars) => {
      void qc.invalidateQueries({ queryKey: threadKey(vars.threadId) });
      void qc.invalidateQueries({ queryKey: THREADS_KEY });
    },
  });
}
